/**
 * Rules for the persisted store/group focus and the scope indicator.
 *
 * Pure on purpose — `verify_focus_rules.ts` proves them without a DOM, and
 * `ScopeContext` / `AppLayout` only wire them up.
 */

/**
 * How far along the dealer list is. The distinction that matters is between
 * "empty" and "not yet received" — conflating them is what deleted people's
 * stored store focus on every page load.
 */
export type FocusLoadState = "auth-pending" | "loading" | "loaded" | "failed";

/**
 * Whether a stored dealer focus should be discarded.
 *
 * This only ever says yes for a list that actually arrived, actually has
 * dealers in it, and genuinely doesn't contain the stored one — a dealer that
 * was removed, or focus left behind by a different group.
 *
 * It deliberately says NO while auth is still resolving, while the request is
 * in flight, when the request failed, and when the list came back empty. None
 * of those are evidence the dealer is gone, and persisted user state is not
 * something to delete on a guess.
 */
export function shouldDropStoredDealerFocus(args: {
  state: FocusLoadState;
  storedDealerId: string | null;
  dealersInFocus: string[];
}): boolean {
  const { state, storedDealerId, dealersInFocus } = args;
  if (!storedDealerId) return false;
  if (state !== "loaded") return false;
  if (dealersInFocus.length === 0) return false;
  return !dealersInFocus.includes(storedDealerId);
}

/**
 * The text and emphasis for the scope indicator.
 *
 * `narrowed` drives the styling: a filtered view has to look filtered. On a
 * phone the picker lives in a closed drawer, so this chip is the only thing
 * that can say "you are not looking at everything".
 */
export function scopeLabel(args: {
  groupName: string | null;
  dealerName: string | null;
  dealerCount: number;
}): { text: string; narrowed: boolean } {
  const { groupName, dealerName, dealerCount } = args;
  if (dealerName) return { text: dealerName, narrowed: true };
  if (groupName) return { text: groupName, narrowed: true };
  if (dealerCount > 1) return { text: `All ${dealerCount} stores`, narrowed: false };
  return { text: "All stores", narrowed: false };
}
